import { MarkerFeature } from "../../../utils/models/MarkerFeature";
import { LineStringFeature } from "../../../utils/models/LineStringFeature";
import {
  lineString,
  point,
  nearestPointOnLine,
  lineSplit,
  distance as turfDistance
} from "@turf/turf";
import type { Feature, Point } from "geojson";

export interface InsertionPoint {
  lng: number;
  lat: number;
  lineStringId: string;
  insertIndex: number;
}

export interface NearestLineResult {
  lineId: string;
  routeId: string;
  distance: number;
  insertionPoint: InsertionPoint;
  nearestPoint: Feature<Point>;
}

export interface RouteData {
  id: string;
  markers: MarkerFeature[];
  lineStrings: LineStringFeature[];
}

export class RouteManager {
  /**
   * Calculate the nearest point on a line segment to a given point
   */
  private getNearestPointOnSegment(
    point: number[],
    segmentStart: number[],
    segmentEnd: number[]
  ): number[] {
    const [px, py] = point;
    const [x1, y1] = segmentStart;
    const [x2, y2] = segmentEnd;

    const dx = x2 - x1;
    const dy = y2 - y1;

    if (dx === 0 && dy === 0) {
      // Segment start and end are the same point
      return segmentStart;
    }

    const t = Math.max(0, Math.min(1,
      ((px - x1) * dx + (py - y1) * dy) / (dx * dx + dy * dy)
    ));

    return [
      x1 + t * dx,
      y1 + t * dy
    ];
  }

  /** Strip consecutive duplicate vertices — avoids zero-length segments */
  private dedupeCoords(coords: [number, number][]): [number, number][] {
    const out: [number, number][] = [];
    for (const c of coords) {
      if (!out.length || out[out.length - 1][0] !== c[0] ||
        out[out.length - 1][1] !== c[1]) {
        out.push(c);
      }
    }
    return out;
  }

  /**
   * Find the nearest line to a given point across all routes
   *
   * Note: This implementation filters out duplicate consecutive coordinates to avoid
   * the "coordinates must contain numbers" error in @turf/nearest-point-on-line
   * that occurs with zero-length segments in versions before March 2025.
   */
  findNearestLine(
    routes: { [key: string]: RouteData },
    clickPoint: [number, number],
    threshold: number
  ): NearestLineResult | null {
    let nearestResult: NearestLineResult | null = null;
    let minDistance = Infinity;

    // Validate click point first
    if (!Array.isArray(clickPoint) ||
      clickPoint.length < 2 ||
      typeof clickPoint[0] !== 'number' ||
      typeof clickPoint[1] !== 'number' ||
      isNaN(clickPoint[0]) ||
      isNaN(clickPoint[1]) ||
      !isFinite(clickPoint[0]) ||
      !isFinite(clickPoint[1])) {
      console.error('Invalid click point:', clickPoint);
      return null;
    }

    // Ensure click point is exactly 2D
    const cleanClickPoint: [number, number] = [
      parseFloat(clickPoint[0].toFixed(10)),
      parseFloat(clickPoint[1].toFixed(10))
    ];

    for (const [routeId, route] of Object.entries(routes)) {
      for (let lineIndex = 0; lineIndex < route.lineStrings.length; lineIndex++) {
        const lineStringFeature = route.lineStrings[lineIndex];
        const line = lineStringFeature.lineStringGeometry.coordinates;

        if (!Array.isArray(line) || line.length < 2) continue;

        // Deep validation and conversion to 2D coordinates
        const validLine: [number, number][] = [];
        let invalidCount = 0;
        let duplicateCount = 0;

        for (let i = 0; i < line.length; i++) {
          const coord = line[i];

          // Skip if not an array
          if (!Array.isArray(coord)) {
            invalidCount++;
            continue;
          }

          // Skip if insufficient length
          if (coord.length < 2) {
            invalidCount++;
            continue;
          }

          // Extract and validate longitude
          let lng: number;
          if (typeof coord[0] === 'number') {
            lng = coord[0];
          } else if (coord[0] != null) {
            lng = parseFloat(String(coord[0]));
          } else {
            invalidCount++;
            continue;
          }

          // Extract and validate latitude
          let lat: number;
          if (typeof coord[1] === 'number') {
            lat = coord[1];
          } else if (coord[1] != null) {
            lat = parseFloat(String(coord[1]));
          } else {
            invalidCount++;
            continue;
          }

          // Validate the numbers
          if (isNaN(lng) || isNaN(lat) || !isFinite(lng) || !isFinite(lat)) {
            invalidCount++;
            continue;
          }

          // Validate reasonable coordinate ranges
          if (Math.abs(lng) > 180 || Math.abs(lat) > 90) {
            invalidCount++;
            continue;
          }

          // Round to reasonable precision and ensure exactly 2D
          const newCoord: [number, number] = [
            parseFloat(lng.toFixed(10)),
            parseFloat(lat.toFixed(10))
          ];

          // Skip duplicate consecutive coordinates (zero-length segments)
          if (validLine.length > 0) {
            const lastCoord = validLine[validLine.length - 1];
            if (lastCoord[0] === newCoord[0] && lastCoord[1] === newCoord[1]) {
              duplicateCount++;
              continue;
            }
          }

          validLine.push(newCoord);
        }

        // Log summary if we found invalid or duplicate coordinates
        if (invalidCount > 0 || duplicateCount > 0) {
          console.warn(`Route ${routeId}, line ${lineIndex}: filtered out ${invalidCount} invalid coordinates and ${duplicateCount} duplicate consecutive points`);
        }

        if (validLine.length < 2) {
          console.warn(`Route ${routeId}, line ${lineIndex}: not enough valid coordinates (${validLine.length})`);
          continue;
        }

        try {
          // Now that we've filtered duplicates, we can try using Turf again
          // Create proper GeoJSON features for Turf
          const lineFeature = lineString(validLine);
          const pointFeature = point(cleanClickPoint);

          let nearestPointResult;
          let distanceInMeters;

          try {
            // Try using Turf's nearestPointOnLine
            nearestPointResult = nearestPointOnLine(lineFeature, pointFeature);
            distanceInMeters = turfDistance(pointFeature, nearestPointResult, { units: 'meters' });
          } catch (turfError) {
            console.warn(`Turf failed for route ${routeId}, line ${lineIndex}, falling back to manual calculation:`, turfError);

            // Fallback to manual calculation
            let minSegmentDistance = Infinity;
            let nearestSegmentPoint: [number, number] | null = null;
            let nearestSegmentIndex = 0;

            for (let i = 0; i < validLine.length - 1; i++) {
              const segmentStart = validLine[i];
              const segmentEnd = validLine[i + 1];

              // Calculate nearest point on segment
              const segmentPoint = this.getNearestPointOnSegment(
                cleanClickPoint,
                segmentStart,
                segmentEnd
              );

              // Ensure the result is exactly 2D with valid numbers
              const cleanSegmentPoint: [number, number] = [
                parseFloat(segmentPoint[0].toFixed(10)),
                parseFloat(segmentPoint[1].toFixed(10))
              ];

              // Validate the segment point
              if (isNaN(cleanSegmentPoint[0]) || isNaN(cleanSegmentPoint[1]) ||
                !isFinite(cleanSegmentPoint[0]) || !isFinite(cleanSegmentPoint[1])) {
                console.warn(`Invalid segment point calculated for segment ${i}`);
                continue;
              }

              // Calculate distance manually
              const dx = cleanSegmentPoint[0] - cleanClickPoint[0];
              const dy = cleanSegmentPoint[1] - cleanClickPoint[1];
              const segmentDistance = Math.sqrt(dx * dx + dy * dy);

              if (segmentDistance < minSegmentDistance) {
                minSegmentDistance = segmentDistance;
                nearestSegmentPoint = cleanSegmentPoint;
                nearestSegmentIndex = i;
              }
            }

            if (!nearestSegmentPoint) {
              console.warn(`Could not find nearest point on line for route ${routeId}, line ${lineIndex}`);
              continue;
            }

            // Convert to meters (approximate conversion for small distances)
            const avgLat = (cleanClickPoint[1] + nearestSegmentPoint[1]) / 2;
            const degToMetersLat = 111111;
            const degToMetersLng = 111111 * Math.cos(avgLat * Math.PI / 180);

            const dxMeters = (nearestSegmentPoint[0] - cleanClickPoint[0]) * degToMetersLng;
            const dyMeters = (nearestSegmentPoint[1] - cleanClickPoint[1]) * degToMetersLat;
            distanceInMeters = Math.sqrt(dxMeters * dxMeters + dyMeters * dyMeters);

            // Create the nearestPoint feature manually
            nearestPointResult = {
              type: 'Feature',
              properties: {},
              geometry: {
                type: 'Point',
                coordinates: nearestSegmentPoint
              }
            };
          }

          if (distanceInMeters < minDistance) {
            minDistance = distanceInMeters;

            // Find which segment contains the nearest point
            const insertIndex = this.findInsertIndex(validLine, nearestPointResult.geometry.coordinates);

            nearestResult = {
              lineId: `route-${routeId}-line-${lineIndex}`,
              routeId,
              distance: distanceInMeters,
              nearestPoint: nearestPointResult,
              insertionPoint: {
                lng: nearestPointResult.geometry.coordinates[0],
                lat: nearestPointResult.geometry.coordinates[1],
                lineStringId: lineStringFeature.lineStringProperties.id || lineIndex.toString(),
                insertIndex: lineIndex + 1  // Marker should be inserted after the lineIndex'th marker
              }
            };
          }
        } catch (error) {
          // Skip this line if there's an error
          console.error(`Error processing route ${routeId}, line ${lineIndex}:`, error);
          continue;
        }
      }
    }

    // Convert threshold from km to meters for comparison
    return minDistance <= threshold * 1000 ? nearestResult : null;
  }

  /**
   * Calculate where a new marker should be inserted in a route
   */
  calculateInsertionPoint(
    lineStringFeature: LineStringFeature,
    clickPoint: [number, number]
  ): InsertionPoint {
    const line = lineStringFeature.lineStringGeometry.coordinates;
    const nearestPoint = nearestPointOnLine(
      lineString(line),
      point(clickPoint)
    );

    const insertIndex = this.findInsertIndex(line, nearestPoint.geometry.coordinates);

    return {
      lng: nearestPoint.geometry.coordinates[0],
      lat: nearestPoint.geometry.coordinates[1],
      lineStringId: lineStringFeature.lineStringProperties.id || '',
      insertIndex
    };
  }

  /**
   * Split a line at a given point, returning the two resulting line segments
   */
  splitLineAtPoint(
    lineStringFeature: LineStringFeature,
    splitPoint: [number, number]
  ): { before: LineStringFeature; after: LineStringFeature } | null {
    // Validate split point
    if (!Array.isArray(splitPoint) ||
        splitPoint.length < 2 ||
        typeof splitPoint[0] !== 'number' ||
        typeof splitPoint[1] !== 'number' ||
        isNaN(splitPoint[0]) ||
        isNaN(splitPoint[1])) {
      console.warn('Invalid split point:', splitPoint);
      return null;
    }

    // Validate line coordinates
    const coords = lineStringFeature.lineStringGeometry.coordinates;
    const validCoords = coords.filter(coord => {
      return Array.isArray(coord) &&
             coord.length >= 2 &&
             typeof coord[0] === 'number' &&
             typeof coord[1] === 'number' &&
             !isNaN(coord[0]) &&
             !isNaN(coord[1]);
    });

    if (validCoords.length < 2) {
      console.warn('Invalid line coordinates for splitting');
      return null;
    }

    try {
      const line = lineString(validCoords);
      const pt = point(splitPoint);

    const split = lineSplit(line, pt);

    if (split.features.length === 2) {
      const beforeCoords = split.features[0].geometry.coordinates;
      const afterCoords = split.features[1].geometry.coordinates;

      // Ensure the split point is included in both segments
      if (beforeCoords[beforeCoords.length - 1][0] !== splitPoint[0] ||
          beforeCoords[beforeCoords.length - 1][1] !== splitPoint[1]) {
        beforeCoords.push(splitPoint);
      }

      if (afterCoords[0][0] !== splitPoint[0] ||
          afterCoords[0][1] !== splitPoint[1]) {
        afterCoords.unshift(splitPoint);
      }

      return {
        before: LineStringFeature.coordsOnlyFeature(beforeCoords),
        after: LineStringFeature.coordsOnlyFeature(afterCoords)
      };
    }

    return null;
    } catch (error) {
      console.warn('Error splitting line:', error);
      return null;
    }
  }

  /**
   * Find the optimal index to insert a new marker in the markers array
   */
  findMarkerInsertIndex(
    markers: MarkerFeature[],
    insertPoint: [number, number],
    lineIndex: number
  ): number {
    // If inserting into the first line segment, insert after the first marker
    // If inserting into the nth line segment, insert after the nth marker
    return Math.min(lineIndex + 1, markers.length);
  }

  /**
   * Validate if a route has valid markers and line strings
   */
  validateRoute(route: RouteData): { valid: boolean; errors: string[] } {
    const errors: string[] = [];

    if (route.markers.length === 0) {
      errors.push("Route has no markers");
    }

    if (route.lineStrings.length !== route.markers.length - 1 && route.markers.length > 1) {
      errors.push(`Route has ${route.markers.length} markers but ${route.lineStrings.length} line segments`);
    }

    // Check for duplicate marker IDs
    const markerIds = new Set<string>();
    for (const marker of route.markers) {
      if (markerIds.has(marker.markerProperties.id)) {
        errors.push(`Duplicate marker ID: ${marker.markerProperties.id}`);
      }
      markerIds.add(marker.markerProperties.id);
    }

    return {
      valid: errors.length === 0,
      errors
    };
  }

  /**
   * Calculate total distance of a route
   */
  calculateRouteDistance(route: RouteData): number {
    let totalDistance = 0;

    for (const lineString of route.lineStrings) {
      const coords = lineString.lineStringGeometry.coordinates;
      for (let i = 0; i < coords.length - 1; i++) {
        totalDistance += turfDistance(
          point(coords[i]),
          point(coords[i + 1]),
          { units: 'meters' }
        );
      }
    }

    return totalDistance;
  }

  /**
   * Find which segment of a line contains a given point
   */
  private findInsertIndex(lineCoords: number[][], targetPoint: number[]): number {
    let insertIndex = 1; // Default to after first point

    for (let i = 0; i < lineCoords.length - 1; i++) {
      const segmentLine = lineString([lineCoords[i], lineCoords[i + 1]]);
      const segmentNearest = nearestPointOnLine(segmentLine, point(targetPoint));

      // Check if this segment contains our target point
      const segmentDist = turfDistance(
        point(targetPoint),
        point(segmentNearest.geometry.coordinates),
        { units: 'meters' }
      );

      if (segmentDist < 0.1) { // Very close, this is our segment
        insertIndex = i + 1;
        break;
      }
    }

    return insertIndex;
  }
}
